// QuickStudyCropModal — modal de crop mobile-first usado depois que o aluno
// captura uma foto (câmera) ou anexa imagem. Permite arrastar handles nos 4
// cantos pra delimitar a área da dúvida (ex: recortar UMA questão da prova
// inteira). Touch + mouse. Saída: imagem cropada em base64 JPEG.
//
// Decisão: NÃO usar lib externa (react-image-crop ~30KB). É <300 linhas de
// canvas + pointer events. Mantém bundle enxuto.

import { useEffect, useRef, useState } from "preact/hooks";
import { Check, Loader2, RotateCcw, X } from "lucide-react";
import type { QuickStudyImageInput } from "./api";

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  /** Imagem original (vinda de File ou data URL). */
  source: File | string;
  /** Quality JPEG do output (default 0.85). */
  quality?: number;
  /** Tamanho máximo do maior lado do output (default 1600). */
  maxDim?: number;
  onCancel: () => void;
  onConfirm: (img: QuickStudyImageInput) => void;
}

type DragKind = "move" | "nw" | "ne" | "sw" | "se";

const HANDLE = 22; // hitbox dos handles em px na tela
const MIN_CROP_PX = 40; // tamanho mínimo do crop nas coords do canvas

export function QuickStudyCropModal({
  source,
  quality = 0.85,
  maxDim = 1600,
  onCancel,
  onConfirm
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Rect | null>(null);
  // Tamanho de exibição (CSS px) do canvas — usado pra mapear ponteiro pra
  // coords da imagem original.
  const [displaySize, setDisplaySize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0
  });
  const [busy, setBusy] = useState(false);
  const [dragKind, setDragKind] = useState<DragKind | null>(null);
  const dragStart = useRef<{ pointerX: number; pointerY: number; cropStart: Rect } | null>(null);

  // Carrega a imagem-fonte e mede.
  useEffect(() => {
    let cancelled = false;
    let revokeUrl: string | null = null;
    (async () => {
      const url = typeof source === "string" ? source : URL.createObjectURL(source);
      if (typeof source !== "string") revokeUrl = url;
      const el = new Image();
      el.onload = () => {
        if (cancelled) return;
        setImg(el);
        // Crop inicial = 80% centralizado.
        const cw = el.naturalWidth;
        const ch = el.naturalHeight;
        const w = Math.floor(cw * 0.8);
        const h = Math.floor(ch * 0.8);
        setCrop({
          x: Math.floor((cw - w) / 2),
          y: Math.floor((ch - h) / 2),
          w,
          h
        });
      };
      el.onerror = () => {
        if (!cancelled) onCancel();
      };
      el.src = url;
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [source]);

  // Pinta o canvas com a imagem + overlay escuro fora do crop.
  useEffect(() => {
    if (!img || !crop) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Ajuste de display size respeitando viewport. Em mobile a área útil é
    // muito menor — limita altura pra deixar espaço pros botões.
    const maxW = Math.min(window.innerWidth - 32, 720);
    const maxH = window.innerHeight - 220;
    let dw = img.naturalWidth;
    let dh = img.naturalHeight;
    if (dw > maxW) {
      dh = Math.round((dh * maxW) / dw);
      dw = maxW;
    }
    if (dh > maxH) {
      dw = Math.round((dw * maxH) / dh);
      dh = maxH;
    }
    canvas.width = dw;
    canvas.height = dh;
    setDisplaySize({ w: dw, h: dh });

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Desenha imagem
    ctx.drawImage(img, 0, 0, dw, dh);
    // Overlay escuro fora do crop
    const scale = dw / img.naturalWidth;
    const cx = crop.x * scale;
    const cy = crop.y * scale;
    const cwS = crop.w * scale;
    const chS = crop.h * scale;
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(0, 0, dw, cy);
    ctx.fillRect(0, cy + chS, dw, dh - (cy + chS));
    ctx.fillRect(0, cy, cx, chS);
    ctx.fillRect(cx + cwS, cy, dw - (cx + cwS), chS);
    // Borda do crop
    ctx.strokeStyle = "#8b5cf6";
    ctx.lineWidth = 2;
    ctx.strokeRect(cx, cy, cwS, chS);
    // Linhas guia (rule of thirds)
    ctx.strokeStyle = "rgba(255,255,255,0.4)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      const lx = cx + (cwS / 3) * i;
      ctx.beginPath();
      ctx.moveTo(lx, cy);
      ctx.lineTo(lx, cy + chS);
      ctx.stroke();
      const ly = cy + (chS / 3) * i;
      ctx.beginPath();
      ctx.moveTo(cx, ly);
      ctx.lineTo(cx + cwS, ly);
      ctx.stroke();
    }
  }, [img, crop]);

  function pointerToImageCoords(e: PointerEvent): { x: number; y: number } | null {
    if (!img) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const scale = img.naturalWidth / rect.width;
    return { x: px * scale, y: py * scale };
  }

  function detectDragKind(e: PointerEvent): DragKind | null {
    if (!img || !crop) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scale = img.naturalWidth / rect.width;
    const pointerX = e.clientX - rect.left;
    const pointerY = e.clientY - rect.top;
    const cx = crop.x / scale;
    const cy = crop.y / scale;
    const cw = crop.w / scale;
    const ch = crop.h / scale;
    // Handles dos 4 cantos
    const handles: Array<[DragKind, number, number]> = [
      ["nw", cx, cy],
      ["ne", cx + cw, cy],
      ["sw", cx, cy + ch],
      ["se", cx + cw, cy + ch]
    ];
    for (const [kind, hx, hy] of handles) {
      if (Math.abs(pointerX - hx) <= HANDLE && Math.abs(pointerY - hy) <= HANDLE) {
        return kind;
      }
    }
    if (
      pointerX >= cx &&
      pointerX <= cx + cw &&
      pointerY >= cy &&
      pointerY <= cy + ch
    ) {
      return "move";
    }
    return null;
  }

  function onPointerDown(e: PointerEvent) {
    if (!img || !crop) return;
    const kind = detectDragKind(e);
    if (!kind) return;
    e.preventDefault();
    setDragKind(kind);
    dragStart.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      cropStart: { ...crop }
    };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragKind || !dragStart.current || !img || !crop) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scale = img.naturalWidth / rect.width;
    const dx = (e.clientX - dragStart.current.pointerX) * scale;
    const dy = (e.clientY - dragStart.current.pointerY) * scale;
    const start = dragStart.current.cropStart;
    let nx = start.x;
    let ny = start.y;
    let nw = start.w;
    let nh = start.h;
    if (dragKind === "move") {
      nx = start.x + dx;
      ny = start.y + dy;
    } else if (dragKind === "nw") {
      nx = start.x + dx;
      ny = start.y + dy;
      nw = start.w - dx;
      nh = start.h - dy;
    } else if (dragKind === "ne") {
      ny = start.y + dy;
      nw = start.w + dx;
      nh = start.h - dy;
    } else if (dragKind === "sw") {
      nx = start.x + dx;
      nw = start.w - dx;
      nh = start.h + dy;
    } else if (dragKind === "se") {
      nw = start.w + dx;
      nh = start.h + dy;
    }
    // Mínimo + clamp aos bounds da imagem
    if (nw < MIN_CROP_PX) {
      if (dragKind === "nw" || dragKind === "sw") nx = start.x + start.w - MIN_CROP_PX;
      nw = MIN_CROP_PX;
    }
    if (nh < MIN_CROP_PX) {
      if (dragKind === "nw" || dragKind === "ne") ny = start.y + start.h - MIN_CROP_PX;
      nh = MIN_CROP_PX;
    }
    if (nx < 0) {
      if (dragKind === "move") nx = 0;
      else {
        nw += nx;
        nx = 0;
      }
    }
    if (ny < 0) {
      if (dragKind === "move") ny = 0;
      else {
        nh += ny;
        ny = 0;
      }
    }
    if (nx + nw > img.naturalWidth) {
      if (dragKind === "move") nx = img.naturalWidth - nw;
      else nw = img.naturalWidth - nx;
    }
    if (ny + nh > img.naturalHeight) {
      if (dragKind === "move") ny = img.naturalHeight - nh;
      else nh = img.naturalHeight - ny;
    }
    setCrop({
      x: Math.round(nx),
      y: Math.round(ny),
      w: Math.round(nw),
      h: Math.round(nh)
    });
  }

  function onPointerUp(e: PointerEvent) {
    setDragKind(null);
    dragStart.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  }

  function resetCrop() {
    if (!img) return;
    setCrop({ x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight });
  }

  async function confirmCrop() {
    if (!img || !crop) return;
    setBusy(true);
    try {
      // Renderiza só a região do crop, então redimensiona pra maxDim.
      let outW = crop.w;
      let outH = crop.h;
      if (outW > maxDim || outH > maxDim) {
        const ratio =
          outW >= outH ? maxDim / outW : maxDim / outH;
        outW = Math.round(outW * ratio);
        outH = Math.round(outH * ratio);
      }
      const out = document.createElement("canvas");
      out.width = outW;
      out.height = outH;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("Canvas sem contexto 2D.");
      ctx.drawImage(
        img,
        crop.x,
        crop.y,
        crop.w,
        crop.h,
        0,
        0,
        outW,
        outH
      );
      const blob: Blob = await new Promise((resolve, reject) =>
        out.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar JPEG."))),
          "image/jpeg",
          quality
        )
      );
      const base64 = await blobToBase64(blob);
      onConfirm({
        mimeType: "image/jpeg",
        base64,
        sizeBytes: blob.size
      });
    } finally {
      setBusy(false);
    }
  }

  // ESC fecha
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div class="fixed inset-0 z-[110] flex flex-col bg-black/95 backdrop-blur-sm">
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancelar"
            title="Cancelar"
            class="rounded-md p-1.5 text-white hover:bg-white/10"
          >
            <X className="size-5" />
          </button>
          <span class="text-sm font-bold text-white">Ajustar área da dúvida</span>
        </div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={resetCrop}
            aria-label="Resetar"
            title="Resetar (selecionar imagem inteira)"
            class="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-[11px] font-semibold text-white hover:bg-white/10"
          >
            <RotateCcw className="size-3.5" />
            Tudo
          </button>
        </div>
      </header>

      <div
        ref={overlayRef}
        class="flex flex-1 items-center justify-center overflow-hidden p-4"
      >
        {!img ? (
          <div class="flex items-center gap-2 text-sm text-white">
            <Loader2 className="size-4 animate-spin" /> Carregando imagem…
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown as any}
            onPointerMove={onPointerMove as any}
            onPointerUp={onPointerUp as any}
            onPointerCancel={onPointerUp as any}
            style={{
              touchAction: "none",
              maxWidth: "100%",
              maxHeight: "100%",
              cursor: dragKind === "move" ? "grabbing" : "crosshair"
            }}
            class="rounded-md shadow-2xl"
          />
        )}
      </div>

      {/* Hint mobile */}
      {img && (
        <div class="px-4 pb-2 text-center text-[11px] text-white/70">
          Arraste os cantos pra ajustar. Toque dentro pra mover.
        </div>
      )}

      <footer class="flex shrink-0 items-center justify-between gap-2 border-t border-white/10 bg-black/40 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          class="rounded-md border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirmCrop}
          disabled={busy || !img || !crop}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          Confirmar
        </button>
      </footer>
    </div>
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader falhou."));
    reader.readAsDataURL(blob);
  });
}
